import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { Box, Button, Column, Row, Spacing, TextButton, Tooltip, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { DeckStack } from '../DeckStack/DeckStack';
import { KIND_ICON } from '../RevenueOpportunitiesSection.constants';
import { useOpportunityDeckMotion } from './hooks/useOpportunityDeckMotion';
import styles from './OpportunityDeck.module.scss';
import { ACTION_BI_NAME_BY_KIND, CONFIDENCE_BARS, filledConfidenceBars } from './OpportunityDeck.utils';
export const GetOpportunityDeckTID = (parentTestId)=>getTestIds(parentTestId);
export const OpportunityDeck = (param)=>{
    let { card: nextCard, onAction, onNext, dismiss, isBusy, compact } = param;
    const { OpportunityDeckTestIds } = useMemo(()=>GetOpportunityDeckTID(), []);
    const { t: texts } = useLocaleKeys('ai.pulse.section');
    const { shownCard: card, isLeaving, isEntering } = useOpportunityDeckMotion(nextCard);
    const KindIcon = KIND_ICON[card.kind];
    const filledBars = filledConfidenceBars(card.confidenceBand);
    return /*#__PURE__*/ _jsxs(DeckStack, {
        border: "ai",
        compact: compact,
        settling: isEntering,
        testId: OpportunityDeckTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                gap: Spacing.Space12,
                className: cn(styles.cardText, {
                    [styles.leaving]: isLeaving,
                    [styles.entering]: isEntering
                }),
                "aria-hidden": isLeaving,
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        gap: Spacing.Space8,
                        alignItems: "center",
                        children: [
                            /*#__PURE__*/ _jsx(Box, {
                                inline: true,
                                alignItems: "center",
                                justifyContent: "center",
                                className: styles.kindIcon,
                                children: /*#__PURE__*/ _jsx(KindIcon, {
                                    size: 18
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                weight: "light",
                                skin: "secondary",
                                testId: OpportunityDeckTestIds.Type(),
                                children: card.typeLabel
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: card.confidenceLabel,
                                placement: "top",
                                children: /*#__PURE__*/ _jsx(Row, {
                                    gap: Spacing.Space2,
                                    alignItems: "end",
                                    className: styles.confidence,
                                    role: "img",
                                    "aria-label": card.confidenceLabel,
                                    testId: OpportunityDeckTestIds.Confidence(),
                                    children: CONFIDENCE_BARS.map((bar, index)=>/*#__PURE__*/ _jsx(Box, {
                                            className: cn(styles.confidenceBar, styles[bar], {
                                                [styles.filled]: index < filledBars
                                            })
                                        }, bar))
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: Spacing.Space4,
                        children: [
                            /*#__PURE__*/ _jsxs(Typography, {
                                variant: "h2",
                                weight: "normal",
                                testId: OpportunityDeckTestIds.Amount(),
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        as: "span",
                                        variant: "h2",
                                        weight: "normal",
                                        className: styles.amount,
                                        children: isEntering ? card.amountFrom : card.amount
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        as: "span",
                                        variant: "h6",
                                        weight: "normal",
                                        children: texts.perYear()
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "h6",
                                weight: "normal",
                                testId: OpportunityDeckTestIds.Lead(),
                                children: card.lead
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                weight: "light",
                                skin: "secondary",
                                testId: OpportunityDeckTestIds.Description(),
                                children: card.description
                            })
                        ]
                    })
                ]
            }, card.identity),
            /*#__PURE__*/ _jsxs(Row, {
                gap: Spacing.Space8,
                alignItems: "center",
                className: styles.actions,
                children: [
                    card.actionLabel && /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: ACTION_BI_NAME_BY_KIND[card.kind]
                        },
                        testId: OpportunityDeckTestIds.Action(),
                        skin: "primary",
                        size: compact ? 'xsmall' : 'small',
                        disabled: isBusy,
                        onClick: isLeaving ? undefined : onAction,
                        children: card.actionLabel
                    }),
                    dismiss,
                    onNext && /*#__PURE__*/ _jsx(TextButton, {
                        bi: {
                            name: 'pulse_opportunities_next'
                        },
                        testId: OpportunityDeckTestIds.Next(),
                        weight: "normal",
                        underline: "none",
                        variant: compact ? 'paragraph2' : 'paragraph1',
                        className: styles.next,
                        onClick: onNext,
                        children: texts.next()
                    })
                ]
            })
        ]
    });
};
